<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { api, type UserAccountAllocation, type SaveAllocationRequest } from '../api/client'
import { useAuth } from '../composables/useAuth'
import { usePortfolio } from '../composables/usePortfolio'
import { useViewAs } from '../composables/useViewAs'

const { isAuthenticated, isViewer, user, login, verifyCode, resendCode } = useAuth()
const { portfolio } = usePortfolio()
// Task 7 (linked users): while the superuser views as a linked user, the HL
// card connects THAT user's HL via the admin endpoints, which never set a
// session cookie, so the superuser stays signed in as themselves.
const { target: viewAsTarget } = useViewAs()
// Final fix wave Minor 7: the admin connect endpoints only accept the
// acting superuser's OWN linked users (R13); /me says so via
// viewingAs.canConnectHl. For anyone else the form would always 403, so show
// a note instead. Fails closed when /me carries no viewingAs block.
const viewAsCanConnectHl = computed(() => user.value?.viewingAs?.canConnectHl === true)
const viewAsHolderOnly = computed(() => viewAsTarget.value !== null && !viewAsCanConnectHl.value)
const fund = computed<'BGA' | 'JH'>(() => portfolio.value === 'jh' ? 'JH' : 'BGA')

// Re-load account data when user logs in
watch(isAuthenticated, (authed) => {
  if (authed) {
    error.value = null
    loadData()
  }
})
const showAllocInfo = ref(false)
const loginEmail = ref('')
const loginHlUserId = ref('')
const loginDob = ref('')
const loginPassword = ref('')
const loginSecureNumber = ref('')
const loginError = ref<string | null>(null)
const loginLoading = ref(false)
const showPassword = ref(false)

// HL SMS verification step (2026-09-21): after username/DOB/password/secure
// digits HL texts a code to the registered mobile. Phase 1 (handleLogin)
// parks the sign-in server-side; phase 2 (handleVerify) submits the code.
const verificationPending = ref(false)
const verificationPhone = ref<string | null>(null)
const loginCode = ref('')
const verifyNotice = ref<string | null>(null)

// Signed in to SideWinder (cookie lasts days) but no usable HL session —
// after an API restart, or when a background login parked at HL's SMS
// prompt. Show the HL sign-in card in place of the empty account list.
const hlSignInNeeded = ref(false)
const showHlSignIn = computed(() => !isAuthenticated.value || hlSignInNeeded.value)

async function refreshHlStatus() {
  if (!isAuthenticated.value) { hlSignInNeeded.value = false; return }
  try {
    const s = await api.authHlStatus()
    hlSignInNeeded.value = !s.hlSessionActive
    if (s.accountLocked) {
      // HL disabled the username; the sign-in card stays but every attempt
      // will fail until HL lifts the lock. Say so up front.
      loginError.value = s.lockMessage ?? 'HL has temporarily disabled this username after too many login attempts. Call HL to unlock it, then sign in again.'
    }
    if (!s.hlSessionActive) {
      loginEmail.value = loginEmail.value || user.value?.email || ''
      loginHlUserId.value = loginHlUserId.value || user.value?.hlUsername || ''
      // Task 9 ruling 3 (dead-end fix): while viewing as a linked user,
      // `verificationPending` describes THAT user's own parked HL login —
      // auto-opening the code box here would let the superuser submit a
      // code meant for the target's session, and the admin verify call
      // would 409 anyway (it isn't the same parked login). Show the
      // Connect HL form instead; without a view-as target this is
      // unchanged from before.
      if (s.verificationPending && !viewAsTarget.value) {
        // HL already texted a code for the parked login — go straight to it.
        verificationPending.value = true
        verificationPhone.value = s.maskedPhone
      }
    }
  } catch {
    hlSignInNeeded.value = false
  }
}

function hlConnected() {
  hlSignInNeeded.value = false
  verificationPending.value = false
  loginCode.value = ''
  // The isAuthenticated watcher reloads on a fresh SideWinder sign-in; an
  // HL reconnect while already signed in must reload explicitly.
  if (isAuthenticated.value) loadData()
}

async function handleLogin() {
  loginError.value = null
  verifyNotice.value = null
  loginLoading.value = true
  try {
    if (viewAsTarget.value) {
      const res = await api.adminHlConnect(
        viewAsTarget.value.userId, loginDob.value, loginPassword.value, loginSecureNumber.value)
      if ('verificationRequired' in res && res.verificationRequired) {
        verificationPending.value = true
        verificationPhone.value = res.maskedPhone
        loginCode.value = ''
      } else {
        hlConnected()
      }
      return
    }
    const pending = await login(
      loginEmail.value,
      loginHlUserId.value || undefined,
      loginDob.value || undefined,
      loginPassword.value || undefined,
      loginSecureNumber.value || undefined
    )
    if (pending) {
      verificationPending.value = true
      verificationPhone.value = pending.maskedPhone
      loginCode.value = ''
    } else {
      hlConnected()
    }
  } catch (e: any) {
    loginError.value = e.message ?? 'Login failed'
  } finally {
    loginLoading.value = false
  }
}

async function handleVerify() {
  loginError.value = null
  verifyNotice.value = null
  loginLoading.value = true
  try {
    if (viewAsTarget.value) {
      await api.adminHlVerify(viewAsTarget.value.userId, loginCode.value.trim())
    } else {
      await verifyCode(loginEmail.value, loginCode.value.trim(), loginHlUserId.value || undefined)
    }
    hlConnected()
  } catch (e: any) {
    loginError.value = e.message ?? 'Verification failed'
  } finally {
    loginLoading.value = false
  }
}

async function handleResend() {
  loginError.value = null
  verifyNotice.value = null
  loginLoading.value = true
  try {
    const pending = await resendCode(loginEmail.value, loginHlUserId.value || undefined)
    verificationPhone.value = pending.maskedPhone ?? verificationPhone.value
    verifyNotice.value = `A new code has been sent to ${verificationPhone.value ?? 'your registered mobile'}.`
  } catch (e: any) {
    loginError.value = e.message ?? 'Could not request a new code'
  } finally {
    loginLoading.value = false
  }
}

function cancelVerification() {
  verificationPending.value = false
  loginCode.value = ''
  loginError.value = null
  verifyNotice.value = null
}

interface AccountValuation {
  accountId: number
  name: string
  totalValue: number
  availableCash: number
}

const accounts = ref<AccountValuation[]>([])
const allocations = ref<Map<number, { name: string; amount: number }>>(new Map())
const savedAllocations = ref<UserAccountAllocation[]>([])
const loading = ref(false)
const saving = ref(false)
const error = ref<string | null>(null)
const success = ref<string | null>(null)

const totalAllocated = computed(() => {
  let total = 0
  for (const alloc of allocations.value.values()) total += alloc.amount
  return total
})

const totalInvested = computed(() => {
  return accounts.value.reduce((sum, acc) => sum + (acc.totalValue - acc.availableCash), 0)
})

const totalAccountValue = computed(() => {
  return accounts.value.reduce((sum, acc) => sum + acc.totalValue, 0)
})

const totalAvailableCash = computed(() => {
  return accounts.value.reduce((sum, acc) => sum + acc.availableCash, 0)
})

// Auto-compound (2026-09-27). ON: each account follows the fund weights on
// its own money — its fund holdings plus all its cash — so growth and new
// cash keep compounding per account. OFF: buys stop once an account's
// invested amount reaches its saved allocation; nothing is sold to get back
// under it. Accounts with no allocation are never in the plan.
const autoCompound = ref(true)
// 2026-10-17 (paolo): with Auto-compound ON the planner's CashHeadroom is unlimited, so the
// allocation amount is never a ceiling — the boxes are locked to say so.
const allocLockedTitle = 'Locked while Auto-compound is on — switch it off to edit the ceiling'
// 2026-10-18 (paolo): the amount's OTHER job — membership (£0 = out of the pot/plan) — must stay
// editable while the boxes are locked, so each account has its own tick. Unticking zeroes the
// amount (remembering it for a re-tick); ticking an account that was never allocated defaults to
// its current value rounded up to the nearest £1,000, a sensible ceiling should Auto-compound later
// be switched off. The server contract is unchanged: a saved row with amount > 0 = in.
const rememberedAmounts = new Map<number, number>()
function isInPlan(accountId: number): boolean {
  return (allocations.value.get(accountId)?.amount ?? 0) > 0
}
function defaultAllocationFor(acc: AccountValuation): number {
  return Math.max(1000, Math.ceil(acc.totalValue / 1000) * 1000)
}
function setInPlan(acc: AccountValuation, inPlan: boolean) {
  const current = allocations.value.get(acc.accountId)?.amount ?? 0
  if (inPlan) {
    if (current > 0) return
    updateAllocation(acc.accountId, rememberedAmounts.get(acc.accountId) ?? defaultAllocationFor(acc))
  } else {
    if (current <= 0) return
    rememberedAmounts.set(acc.accountId, current)
    updateAllocation(acc.accountId, 0)
  }
}
// Per-account plan flag state (final-review ruling F5). Missing/failed
// config read => false: never claim per-account sizing is live when unknown.
const perAccountPlan = ref(false)
// Shared-pot user (2026-10-13): the pot honours Auto-compound whatever the per-account flag says —
// ON = the whole pot, OFF = each account's cash counts only up to its allocation headroom
// (SharedPotLegPlanner.CashHeadroom). Missing/failed config read => false.
const sharedPot = ref(false)
const membershipNoun = computed(() => sharedPot.value ? 'pot' : 'plan')
const showCapitalInPlan = computed(() => perAccountPlan.value || sharedPot.value)
const showCompoundInfo = ref(false)
const compoundSaving = ref(false)

const capitalInPlanByAccount = computed(() => {
  const m = new Map<number, number>()
  for (const s of savedAllocations.value) {
    if (s.allocatedAmount <= 0) continue
    const acc = accounts.value.find(a => a.accountId === s.accountId)
    const whole = acc?.totalValue ?? 0
    if (autoCompound.value) { m.set(s.accountId, whole); continue }
    if (sharedPot.value) {
      // Holdings always count; cash only up to the allocation headroom.
      const cash = acc?.availableCash ?? 0
      const invested = Math.max(0, whole - cash)
      m.set(s.accountId, invested + Math.min(cash, Math.max(0, s.allocatedAmount - invested)))
    } else {
      m.set(s.accountId, Math.min(whole, s.allocatedAmount))
    }
  }
  return m
})
const capitalInPlan = computed(() => [...capitalInPlanByAccount.value.values()].reduce((a, b) => a + b, 0))

async function toggleAutoCompound(enabled: boolean) {
  const previous = autoCompound.value
  autoCompound.value = enabled
  compoundSaving.value = true
  error.value = null
  try {
    await api.userSetAutoCompound(enabled)
  } catch (e: any) {
    autoCompound.value = previous
    error.value = e.message
  } finally {
    compoundSaving.value = false
  }
}

const hasChanges = computed(() => {
  for (const [id, alloc] of allocations.value) {
    const saved = savedAllocations.value.find(p => p.accountId === id)
    if (!saved && alloc.amount > 0) return true
    if (saved && saved.allocatedAmount !== alloc.amount) return true
  }
  for (const saved of savedAllocations.value) {
    const alloc = allocations.value.get(saved.accountId)
    if (!alloc || alloc.amount !== saved.allocatedAmount) return true
  }
  return false
})

async function loadData() {
  loading.value = true
  error.value = null
  try {
    const [accs, userAllocs, execConfig] = await Promise.all([
      // Task 9 fix round 1: routed through request() (api.accountValuations)
      // rather than a raw fetch() — the raw fetch never carried
      // X-SideWinder-View-As, so while viewing as, this showed the
      // superuser's OWN HL accounts next to the target's allocations.
      api.accountValuations(),
      api.userAllocations(fund.value),
      // Missing/failed config read keeps the column default (ON) rather
      // than blanking the whole panel.
      Promise.resolve().then(() => api.userExecutionConfig()).catch(() => null),
    ])

    accounts.value = accs
    savedAllocations.value = userAllocs
    autoCompound.value = execConfig?.compoundOtp ?? true
    perAccountPlan.value = execConfig?.perAccountPlan === true
    sharedPot.value = execConfig?.sharedPot === true

    // Initialise allocation map from saved allocations
    const map = new Map<number, { name: string; amount: number }>()
    for (const acc of accs) {
      const saved = userAllocs.find(p => p.accountId === acc.accountId)
      map.set(acc.accountId, {
        name: acc.name,
        amount: saved?.allocatedAmount ?? 0,
      })
    }
    allocations.value = map
  } catch (e: any) {
    error.value = e.message
  } finally {
    loading.value = false
  }
  if (!Array.isArray(accounts.value) || accounts.value.length === 0) await refreshHlStatus()
}

function updateAllocation(accountId: number, amount: number) {
  const alloc = allocations.value.get(accountId)
  if (alloc) {
    alloc.amount = amount
    allocations.value = new Map(allocations.value)
  }
}

async function saveAllocations() {
  saving.value = true
  error.value = null
  success.value = null
  try {
    const allocationRequests: SaveAllocationRequest[] = accounts.value
      .filter(acc => (allocations.value.get(acc.accountId)?.amount ?? 0) > 0)
      .map(acc => ({
        accountId: acc.accountId,
        accountName: acc.name,
        allocatedAmount: allocations.value.get(acc.accountId)?.amount ?? 0,
      }))
    await api.userSaveAllocations(allocationRequests, fund.value)
    // Refresh saved state from server
    savedAllocations.value = await api.userAllocations(fund.value)
    success.value = `Saved: ${allocationRequests.length} account(s), total ${formatGbp(totalAllocated.value)}`
    setTimeout(() => { success.value = null }, 5000)
  } catch (e: any) {
    error.value = e.message
  } finally {
    saving.value = false
  }
}

function formatGbp(value: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(value)
}

onMounted(() => {
  if (isAuthenticated.value) loadData()
})
</script>

<template>
  <div class="account-panel">
    <!-- Login form when not authenticated, or when HL needs a fresh sign-in -->
    <section v-if="showHlSignIn" class="card login-card" data-testid="hl-signin-card">
      <div class="login-header">
        <template v-if="viewAsTarget && viewAsHolderOnly">
          <h2>{{ viewAsTarget.label }}'s HL account isn't connected</h2>
          <p class="login-subtitle" data-testid="viewas-hl-holder-only">
            Only {{ viewAsTarget.label }} can reconnect this HL account, by signing in to Blue Box themselves.
          </p>
        </template>
        <h2 v-else-if="viewAsTarget">Connect {{ viewAsTarget.label }}'s HL account</h2>
        <h2 v-else>{{ isAuthenticated ? 'Reconnect your HL account' : 'Sign in to Blue Box' }}</h2>
        <p v-if="viewAsTarget && !viewAsHolderOnly" class="login-subtitle" data-testid="viewas-hl-note">
          Enter {{ viewAsTarget.label }}'s HL details. HL texts the code to the phone on their HL account. You stay signed in as yourself.
        </p>
        <p v-else-if="!viewAsTarget" class="login-subtitle">
          {{ isAuthenticated
            ? 'HL needs a fresh sign-in (its session expired or the API restarted). Background HL jobs are paused until then.'
            : 'Connect your HL account to start trading' }}
        </p>
      </div>

      <div v-if="loginError && !viewAsHolderOnly" class="error-box">&#9888; {{ loginError }}</div>

      <!-- Phase 2: HL texted a verification code (login-step-three) -->
      <template v-if="viewAsHolderOnly"></template>
      <template v-else-if="verificationPending">
        <p class="verify-intro" data-testid="verify-intro">
          HL has sent a verification code to
          <strong>{{ verificationPhone ?? 'your registered mobile' }}</strong>.
          Enter it below to finish signing in.
        </p>
        <div v-if="verifyNotice" class="success-box">{{ verifyNotice }}</div>

        <div class="form-group">
          <label>Verification Code</label>
          <input v-model="loginCode" type="text" inputmode="numeric" autocomplete="one-time-code"
                 placeholder="Code from HL text message" maxlength="12" data-testid="verify-code"
                 @keyup.enter="handleVerify" />
        </div>

        <button class="login-btn" data-testid="verify-btn" @click="handleVerify"
                :disabled="loginLoading || !loginCode.trim()">
          {{ loginLoading ? 'Verifying...' : 'Verify & Sign In' }}
        </button>

        <div class="verify-links">
          <a v-if="!viewAsTarget" href="#" data-testid="verify-resend" @click.prevent="handleResend">Didn't get a code? Send a new one</a>
          <a href="#" data-testid="verify-cancel" @click.prevent="cancelVerification">Start again</a>
        </div>
      </template>

      <!-- Phase 1: HL credentials -->
      <template v-else>
        <!-- Viewing as: the server uses the target's stored HL user ID. -->
        <template v-if="!viewAsTarget">
          <div class="form-group">
            <label>Email Address</label>
            <input v-model="loginEmail" type="email" placeholder="you@example.com" />
          </div>

          <div class="form-group">
            <label>HL User ID</label>
            <input v-model="loginHlUserId" type="text" placeholder="e.g. Montefa68" />
          </div>
        </template>

        <div class="form-group">
          <label>Date of Birth (DDMMYY)</label>
          <input v-model="loginDob" type="text" placeholder="141268" maxlength="6" />
        </div>

        <div class="form-group">
          <label>HL Password</label>
          <div class="password-wrap">
            <input v-model="loginPassword" :type="showPassword ? 'text' : 'password'"
                   placeholder="Enter your HL password" />
            <span class="show-toggle" @click="showPassword = !showPassword">
              {{ showPassword ? 'Hide' : 'Show' }}
            </span>
          </div>
        </div>

        <div class="form-group">
          <label>Secure Number (6-digit)</label>
          <input v-model="loginSecureNumber" type="text" placeholder="e.g. 141268" maxlength="6" />
        </div>

        <p class="login-hint">HL will text a verification code to your registered mobile after this step.</p>

        <!-- The API issues no session without a real HL sign-in (2026-09-28). -->
        <button class="login-btn" data-testid="login-btn" @click="handleLogin"
                :disabled="loginLoading || (!viewAsTarget && !loginEmail) || !loginDob || !loginPassword || !loginSecureNumber">
          {{ loginLoading ? 'Signing in...' : (viewAsTarget ? 'Connect HL' : 'Sign In') }}
        </button>
      </template>
    </section>

    <!-- Existing account content when authenticated -->
    <template v-else>
      <div v-if="loading" class="loading">Loading accounts...</div>


      <div v-if="error" class="error-box">{{ error }}</div>
      <div v-if="success" class="success-box">{{ success }}</div>

      <section v-if="accounts.length > 0" class="card">
        <h2>Position Allocation <span class="info-icon" data-testid="alloc-info" @click.stop="showAllocInfo = !showAllocInfo">i</span></h2>

        <p v-if="!showCapitalInPlan" class="per-account-off-notice" data-testid="per-account-off-notice">
          Per-account sizing isn't switched on yet &mdash; today's dealing plan still sizes all accounts together. The notes below describe how it works once it is.
        </p>

        <div v-if="showAllocInfo && sharedPot" class="info-panel" data-testid="alloc-info-shared-pot">
          <p><strong>Position Allocation</strong> tells Blue Box which HL accounts make up your <strong>shared pot</strong> &mdash; the dealing plan treats them as one pot of holdings plus cash.</p>

          <ul>
            <li>The <strong>In pot</strong> tick says which accounts are in. An account that is not ticked (allocation <strong>&pound;0</strong>) is never traded &mdash; for example an ISA you hold outside the strategy.</li>
            <li>With Auto-compound <strong>on</strong>, the amount does not matter beyond being above &pound;0: all of a ticked account's holdings and all its cash are used.</li>
            <li>With Auto-compound <strong>off</strong>, the amount is that account's ceiling: its cash counts only up to the allocation, minus what it already has invested.</li>
            <li>Each account can only buy with the cash it actually holds, after a &pound;10 buffer for charges.</li>
          </ul>

          <p><strong>When to update:</strong> after opening or closing an account, or to take an account in or out of the pot (the tick always works). With Auto-compound off, also after deposits or withdrawals. The amount boxes are locked while Auto-compound is on &mdash; switch it off to edit a ceiling. Changes apply from the next dealing plan (08:40 UK).</p>
        </div>

        <div v-else-if="showAllocInfo" class="info-panel">
          <p><strong>Position Allocation</strong> tells Blue Box which HL accounts the dealing plan trades in &mdash; each one follows the fund weights on its own money.</p>

          <ul>
            <li>The <strong>In plan</strong> tick says which accounts are in. An account that is not ticked (allocation <strong>&pound;0</strong>) is never traded &mdash; for example an ISA you hold outside the strategy.</li>
            <li>Each account follows the fund weights on its <strong>own money</strong> &mdash; a &pound;5,000 ISA at 25% BGA buys &pound;1,250 of BGA, whatever the other accounts hold.</li>
            <li>With Auto-compound off the allocation is also that account's ceiling.</li>
            <li>Each account can only buy with the cash it actually holds, after a &pound;10 buffer for charges.</li>
            <li>How much the plan deploys in total depends on <strong>Auto-compound</strong> below.</li>
          </ul>

          <p><strong>When to update:</strong> after opening or closing an account, or to take an account in or out of the plan (the tick always works). With Auto-compound off, also after deposits or withdrawals, since the allocation is then the ceiling. The amount boxes are locked while Auto-compound is on &mdash; switch it off to edit a ceiling. Changes apply from the next dealing plan (08:40 UK).</p>
        </div>

        <!-- Mobile: card layout -->
        <div class="account-cards">
          <div v-for="acc in accounts" :key="'m-' + acc.accountId" class="account-card">
            <div class="ac-header">
              <div class="ac-name">{{ acc.name }}</div>
              <div class="ac-id">ID: {{ acc.accountId }}</div>
            </div>
            <div class="ac-row">
              <span class="ac-label">Invested</span>
              <span class="ac-val val-invested">{{ formatGbp(acc.totalValue - acc.availableCash) }}</span>
            </div>
            <div class="ac-row">
              <span class="ac-label">Cash</span>
              <span class="ac-val val-cash">{{ formatGbp(acc.availableCash) }}</span>
            </div>
            <div class="ac-row">
              <span class="ac-label">Total Value</span>
              <span class="ac-val val-total">{{ formatGbp(acc.totalValue) }}</span>
            </div>
            <div class="ac-alloc">
              <label class="ac-row in-plan-row" :data-testid="`in-plan-row-m-${acc.accountId}`">
                <span class="ac-label">In {{ membershipNoun }}</span>
                <input
                  type="checkbox"
                  class="in-plan-tick"
                  :data-testid="`in-plan-tick-m-${acc.accountId}`"
                  :checked="isInPlan(acc.accountId)"
                  :disabled="isViewer"
                  @change="setInPlan(acc, ($event.target as HTMLInputElement).checked)"
                />
              </label>
              <label class="ac-label">Allocation</label>
              <div class="input-group">
                <span class="currency-prefix">&pound;</span>
                <input
                  type="number"
                  :data-testid="`alloc-input-m-${acc.accountId}`"
                  :value="allocations.get(acc.accountId)?.amount ?? 0"
                  @input="updateAllocation(acc.accountId, Number(($event.target as HTMLInputElement).value))"
                  min="0"
                  step="1000"
                  :disabled="isViewer || autoCompound"
                  :title="autoCompound && !isViewer ? allocLockedTitle : undefined"
                />
              </div>
              <div v-if="showCapitalInPlan && capitalInPlanByAccount.has(acc.accountId)" class="ac-row" :data-testid="`capital-in-plan-${acc.accountId}`">
                <span class="ac-label">In plan</span>
                <span class="ac-val">{{ formatGbp(capitalInPlanByAccount.get(acc.accountId)!) }}</span>
              </div>
            </div>
          </div>
          <!-- Totals card -->
          <div class="account-card totals-card">
            <div class="ac-header"><div class="ac-name">Total</div></div>
            <div class="ac-row">
              <span class="ac-label">Invested</span>
              <span class="ac-val val-invested">{{ formatGbp(totalInvested) }}</span>
            </div>
            <div class="ac-row">
              <span class="ac-label">Cash</span>
              <span class="ac-val val-cash">{{ formatGbp(totalAvailableCash) }}</span>
            </div>
            <div class="ac-row">
              <span class="ac-label">Total Value</span>
              <span class="ac-val val-total">{{ formatGbp(totalAccountValue) }}</span>
            </div>
            <div class="ac-row">
              <span class="ac-label">Allocated</span>
              <span class="ac-val">{{ formatGbp(totalAllocated) }}</span>
            </div>
          </div>
        </div>

        <!-- Desktop: table layout -->
        <table class="desktop-table">
          <thead>
            <tr>
              <th>Account</th>
              <th class="right">Invested</th>
              <th class="right">Cash</th>
              <th class="right">Total Value</th>
              <th class="center">In {{ membershipNoun }}</th>
              <th class="right">Allocation</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="acc in accounts" :key="'d-' + acc.accountId">
              <td>
                {{ acc.name }}
                <div class="account-id">ID: {{ acc.accountId }}</div>
              </td>
              <td class="right val-invested">{{ formatGbp(acc.totalValue - acc.availableCash) }}</td>
              <td class="right val-cash">{{ formatGbp(acc.availableCash) }}</td>
              <td class="right val-total">{{ formatGbp(acc.totalValue) }}</td>
              <td class="center">
                <input
                  type="checkbox"
                  class="in-plan-tick"
                  :data-testid="`in-plan-tick-d-${acc.accountId}`"
                  :checked="isInPlan(acc.accountId)"
                  :disabled="isViewer"
                  :title="`In ${membershipNoun}`"
                  @change="setInPlan(acc, ($event.target as HTMLInputElement).checked)"
                />
              </td>
              <td class="right">
                <div class="input-group">
                  <span class="currency-prefix">&pound;</span>
                  <input
                    type="number"
                    :data-testid="`alloc-input-d-${acc.accountId}`"
                    :value="allocations.get(acc.accountId)?.amount ?? 0"
                    @input="updateAllocation(acc.accountId, Number(($event.target as HTMLInputElement).value))"
                    min="0"
                    step="1000"
                    :disabled="isViewer || autoCompound"
                    :title="autoCompound && !isViewer ? allocLockedTitle : undefined"
                  />
                </div>
                <div v-if="showCapitalInPlan && capitalInPlanByAccount.has(acc.accountId)" class="in-plan-cell" :data-testid="`capital-in-plan-${acc.accountId}`">
                  In plan: {{ formatGbp(capitalInPlanByAccount.get(acc.accountId)!) }}
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="total-row">
              <td><strong>Total</strong></td>
              <td class="right val-invested"><strong>{{ formatGbp(totalInvested) }}</strong></td>
              <td class="right val-cash"><strong>{{ formatGbp(totalAvailableCash) }}</strong></td>
              <td class="right val-total"><strong>{{ formatGbp(totalAccountValue) }}</strong></td>
              <td></td>
              <td class="right"><strong>{{ formatGbp(totalAllocated) }}</strong></td>
            </tr>
          </tfoot>
        </table>

        <p v-if="autoCompound && !isViewer" class="alloc-locked-note" data-testid="alloc-locked-note">
          Allocation ceilings are not used while Auto-compound is on, so the amount boxes are locked. Use the In {{ membershipNoun }} tick to take an account in or out of the {{ membershipNoun }}; switch Auto-compound off to change a ceiling.
        </p>

        <div class="compound-row" data-testid="auto-compound-row">
          <label v-if="!isViewer" class="compound-toggle">
            <input type="checkbox" data-testid="auto-compound-toggle"
                   :checked="autoCompound" :disabled="compoundSaving"
                   @change="toggleAutoCompound(($event.target as HTMLInputElement).checked)" />
            <span>Auto-compound</span>
          </label>
          <span v-else class="compound-readonly" data-testid="auto-compound-readonly">
            Auto-compound: {{ autoCompound ? 'On' : 'Off' }}
          </span>
          <span class="info-icon" data-testid="auto-compound-info"
                @click.stop="showCompoundInfo = !showCompoundInfo">i</span>
          <span v-if="showCapitalInPlan" class="compound-capital" data-testid="capital-in-plan">
            Capital in plan: <strong>{{ formatGbp(capitalInPlan) }}</strong>
            <span class="compound-mode">{{ sharedPot
              ? (autoCompound ? '(whole pot)' : '(cash capped at allocation)')
              : (autoCompound ? '(whole of allocated accounts)' : '(capped at allocation)') }}</span>
          </span>
        </div>
        <p v-if="showCompoundInfo && sharedPot" class="compound-footnote" data-testid="auto-compound-footnote">
          <strong>On</strong> &mdash; the shared pot is everything in your allocated accounts: all fund holdings at their latest
          value plus all cash. Gains, dividends and new deposits are picked up by the next plan, so the whole pot compounds.<br>
          <strong>Off</strong> &mdash; each account's allocation is its ceiling: its cash counts towards the pot only up to the
          allocation minus what it already has invested, and extra cash stays uninvested until you raise the allocation. Growth
          above the allocation stays invested &mdash; the plan never sells to get back under it.
        </p>
        <p v-else-if="showCompoundInfo" class="compound-footnote" data-testid="auto-compound-footnote">
          <strong>On</strong> &mdash; each account follows the fund weights on its own money: its fund holdings at their latest
          value plus all its cash. Gains, dividends and new deposits in an account are picked up by that account's next plan,
          so every account grows in line with the strategy.<br>
          <strong>Off</strong> &mdash; each account's allocation is its ceiling: buys stop once that account's invested amount
          reaches it, and extra cash stays uninvested until you raise the allocation. Growth above the allocation stays
          invested &mdash; the plan never sells to get back under it.
        </p>

        <div v-if="!isViewer" class="actions">
          <button class="save" @click="saveAllocations" :disabled="saving || !hasChanges">
            {{ saving ? 'Saving...' : 'Save Allocation' }}
          </button>
          <span v-if="!hasChanges" class="hint">No changes</span>
        </div>
        <p v-else class="viewer-note" data-testid="viewer-readonly-note">Read-only account</p>
      </section>

      <section v-if="!loading && accounts.length === 0 && !error" class="card">
        <p>No HL accounts found. Check API connection.</p>
      </section>
    </template>
  </div>
</template>

<style scoped>
.card {
  border: 1px solid #333;
  border-radius: 4px;
  padding: 0.75rem;
}

.card h2 {
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* --- Info panel --- */
.info-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px; border-radius: 50%;
  background: #21262d; color: #8b949e; font-size: 0.6rem;
  cursor: pointer; font-style: italic; font-weight: 600;
  margin-left: 4px; vertical-align: middle;
}
.info-icon:hover { background: #30363d; color: #c9d1d9; }

.info-panel {
  background: #0d1117;
  border: 1px solid #21262d;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.82rem;
  color: #8b949e;
  line-height: 1.6;
}
.info-panel p { margin: 0 0 0.5rem 0; }
.info-panel strong { color: #c9d1d9; }
.info-panel ul { margin: 0 0 0.5rem 1.2rem; padding: 0; }
.info-panel li { margin-bottom: 0.3rem; }
.info-panel code { background: #161b22; padding: 1px 4px; border-radius: 3px; font-size: 0.75rem; color: #c9d1d9; }

/* --- Mobile-first: card layout --- */
.account-cards { display: flex; flex-direction: column; gap: 0.75rem; }

.account-card {
  background: #0d1117;
  border: 1px solid #21262d;
  border-radius: 6px;
  padding: 0.75rem;
}
.totals-card { border-color: #444; background: #161b22; }

.ac-header { margin-bottom: 0.5rem; }
.ac-name { font-weight: 600; color: #e6edf3; font-size: 0.95rem; }
.ac-id { color: #555; font-size: 0.7rem; }

.ac-row {
  display: flex;
  justify-content: space-between;
  padding: 0.2rem 0;
  font-size: 0.85rem;
}
.ac-label { color: #6e7681; }
.ac-val { font-weight: 500; font-family: 'JetBrains Mono', monospace; font-size: 0.85rem; }

.ac-alloc {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #21262d;
}
.ac-alloc .ac-label { display: block; margin-bottom: 0.25rem; font-size: 0.7rem; text-transform: uppercase; color: #6e7681; }
.ac-alloc .input-group { justify-content: flex-start; }
.ac-alloc input[type="number"] { width: 100%; }

.val-invested { color: #4caf50; }
.val-cash { color: #8cb8e0; }
.val-total { color: #ccc; }

/* --- Desktop: table layout (hidden on mobile) --- */
.desktop-table { display: none; }

.input-group { display: flex; align-items: center; gap: 0.25rem; }
.currency-prefix { color: #666; font-size: 0.9rem; }

input[type="number"] {
  width: 120px;
  padding: 0.5rem;
  background: #1a1a1a;
  color: #eee;
  border: 1px solid #444;
  border-radius: 4px;
  text-align: right;
  font-size: 1rem;
  box-sizing: border-box;
  min-height: 44px;
}

/* Remove number spinner on mobile for cleaner look */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.compound-row {
  display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem;
  margin-top: 1rem; padding-top: 0.75rem; border-top: 1px solid #21262d;
  font-size: 0.85rem; color: #c9d1d9;
}
.compound-toggle { display: inline-flex; align-items: center; gap: 0.4rem; cursor: pointer; min-height: 44px; }
.compound-toggle input { width: 18px; height: 18px; accent-color: #4caf50; }
.compound-capital { color: #8b949e; flex-basis: 100%; }
.compound-capital strong { color: #e6edf3; font-family: 'JetBrains Mono', monospace; }
.compound-mode { color: #6e7681; font-size: 0.75rem; margin-left: 0.25rem; }
.compound-footnote {
  margin: 0.5rem 0 0; font-size: 0.78rem; line-height: 1.55; color: #8b949e;
  border-left: 2px solid #30363d; padding-left: 0.6rem;
}
.compound-footnote strong { color: #c9d1d9; }
.per-account-off-notice { color: #d29922; font-size: 0.85rem; margin: 0 0 0.75rem; }
.alloc-locked-note { color: #8b949e; font-size: 0.8rem; margin: 0.5rem 0 0; line-height: 1.5; }
.input-group input:disabled { opacity: 0.55; cursor: not-allowed; }
.in-plan-tick { width: 18px; height: 18px; accent-color: #4caf50; cursor: pointer; }
.in-plan-tick:disabled { opacity: 0.55; cursor: not-allowed; }
.in-plan-row { cursor: pointer; min-height: 44px; align-items: center; margin-bottom: 0.25rem; }
.desktop-table th.center, .desktop-table td.center { text-align: center; }
.in-plan-cell { color: #8b949e; font-size: 0.75rem; margin-top: 0.25rem; text-align: right; }

@media (min-width: 768px) {
  .compound-capital { flex-basis: auto; margin-left: auto; }
}

.actions { margin-top: 1rem; display: flex; align-items: center; gap: 0.75rem; }
.hint { color: #666; font-size: 0.85rem; }
.viewer-note { margin-top: 1rem; color: #8b949e; font-size: 0.85rem; font-style: italic; }
.compound-readonly { color: #8b949e; font-size: 0.85rem; }

button.save {
  padding: 0.6rem 1.25rem;
  border: 1px solid #4caf50;
  background: #2a2a2a;
  color: #eee;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  min-height: 44px;
  width: 100%;
}
button.save:hover:not(:disabled) { background: #1b3a1b; }
button.save:disabled { opacity: 0.4; cursor: not-allowed; }

.error-box { padding: 0.75rem; background: #3a1b1b; border: 1px solid #f44336; border-radius: 4px; color: #f44336; margin-bottom: 1rem; font-size: 0.85rem; }
.success-box { padding: 0.75rem; background: #1b3a1b; border: 1px solid #4caf50; border-radius: 4px; color: #4caf50; margin-bottom: 1rem; font-size: 0.85rem; }
.loading { text-align: center; color: #888; font-style: italic; }

/* --- Login form (already mobile-friendly) --- */
.login-card { max-width: 420px; margin: 1rem auto; }
.login-header { text-align: center; margin-bottom: 1.5rem; }
.login-header h2 { margin: 0; font-size: 1.25rem; color: #eee; }
.login-subtitle { color: #666; font-size: 0.85rem; margin-top: 0.25rem; }
.form-group { margin-bottom: 1rem; }
.form-group label { display: block; font-size: 0.75rem; color: #888; text-transform: uppercase; margin-bottom: 0.25rem; }
.form-group input {
  width: 100%; padding: 0.6rem; background: #1a1a1a; color: #eee;
  border: 1px solid #444; border-radius: 4px; font-size: 1rem; box-sizing: border-box;
  min-height: 44px;
}
.password-wrap { position: relative; }
.password-wrap input { padding-right: 60px; }
.show-toggle {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  font-size: 0.85rem; color: #58a6ff; cursor: pointer;
  padding: 0.5rem;
}
.login-btn {
  width: 100%; padding: 0.75rem; background: #238636; border: 1px solid #2ea043;
  border-radius: 4px; color: #fff; font-size: 1rem; font-weight: 600; cursor: pointer;
  min-height: 48px;
}
.login-btn:hover:not(:disabled) { background: #2ea043; }
.login-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.login-hint { color: #666; font-size: 0.8rem; margin: -0.25rem 0 1rem; }
.verify-intro { color: #ccc; font-size: 0.9rem; line-height: 1.4; margin: 0 0 1rem; }
.verify-intro strong { color: #eee; }
.verify-links { display: flex; justify-content: space-between; gap: 1rem; margin-top: 0.75rem; flex-wrap: wrap; }
.verify-links a { color: #58a6ff; font-size: 0.85rem; text-decoration: none; padding: 0.5rem 0; }
.verify-links a:hover { text-decoration: underline; }

/* --- Desktop breakpoint --- */
@media (min-width: 768px) {
  .card { padding: 1rem; }

  .account-cards { display: none; }

  .desktop-table {
    display: table;
    width: 100%;
    border-collapse: collapse;
  }
  .desktop-table th { text-align: left; padding: 0.4rem 0.5rem; border-bottom: 2px solid #333; color: #888; font-size: 0.85rem; font-weight: 500; }
  .desktop-table td { padding: 0.5rem; border-bottom: 1px solid #222; }
  .desktop-table .right { text-align: right; }
  .desktop-table th.right { text-align: right; }
  .desktop-table .account-id { color: #555; font-size: 0.75rem; margin-top: 0.15rem; }
  .desktop-table .total-row td { border-top: 2px solid #444; border-bottom: none; padding-top: 0.75rem; }
  .desktop-table .input-group { justify-content: flex-end; }
  .desktop-table input[type="number"] { width: 120px; }

  button.save { width: auto; }
}
</style>
